import { hyp } from '../../core/exactMath';
import type { GridPoint } from '../../core/types';
import { hash2 } from '../../core/rng';
import type { WorldMap } from '../../world/WorldMap';
import type { BuildingStore } from '../buildings/BuildingStore';
import { buildingDef } from '../buildings/definitions';
import type { PlayerId } from '../ids';
import { nearestOpenTiles } from '../pathing/AStar';
import type { UnitEvent, UnitStore } from '../units/UnitStore';
import { unitDef } from '../units/unitDefs';

/**
 * Infection rules (Phase 7, DECISIONS D68–D72). Explicit and deterministic —
 * never a dice roll:
 *   exposure  each bite from an infected unit adds `biteExposure` to the victim
 *   check     at `infectAt` exposure the victim becomes Infected
 *   infected  loses `bleedPerSecond` health per second; cannot recover (no cure yet)
 *   turn      a unit killed by a bite, or dying while Infected, rises as a Walker
 *             `riseSeconds` later where it fell → hostile to everyone
 */
export const INFECTION = {
  biteExposure: 1,
  infectAt: 3,
  bleedPerSecond: 1,
  riseSeconds: 3,
} as const;

/** Spawning and roaming (placeholder balance, D70). */
export const HORDE = {
  /** Walkers in the spawn zone(s) at the start. */
  initial: 16,
  /** Idle walkers wander within this many tiles of their home. */
  roamRadius: 6,
  /** Seconds a walker stands still between wanders. */
  roamPauseMin: 4,
  roamPauseMax: 10,
  /** Hordes: first at 5 min, then every 2 min, growing by 2 each time (6, 8, 10 … 30). */
  firstWaveSeconds: 300,
  waveEverySeconds: 120,
  waveSize: 6,
  waveGrowth: 2,
  maxWaveSize: 30,
  /** Hard cap on living infected (performance budget). */
  maxInfected: 500,
} as const;

export interface InfectedConfig {
  /** Walkers at the start (default HORDE.initial). */
  readonly initial?: number;
  /** Periodic hordes on (default true). */
  readonly waves?: boolean;
  /** Seed for wander choices (same seed + same commands ⇒ same game). */
  readonly seed?: number;
  /** Phase 9 mode overrides of the horde schedule (defaults: HORDE). */
  readonly firstWaveSeconds?: number;
  readonly waveEverySeconds?: number;
  readonly waveSize?: number;
  readonly waveGrowth?: number;
  readonly maxWaveSize?: number;
}

export type InfectedEvent =
  /** A horde emerged and is marching on `target`'s colony. */
  | { type: 'wave'; index: number; count: number; target: PlayerId; from: GridPoint }
  /** A unit's exposure reached the threshold: it is now Infected. */
  | { type: 'infected'; unitId: number; ownerId: string }
  /** A unit was bitten (exposure grew but is still below the threshold). */
  | { type: 'bitten'; unitId: number; exposure: number }
  /** A fallen unit rose as a Walker. */
  | { type: 'rose'; zombieId: number; at: GridPoint; formerOwner: string };

export interface InfectedSnapshot {
  readonly time: number;
  readonly exposure: Record<string, number>;
  readonly infected: number[];
  readonly rising: { x: number; y: number; t: number; formerOwner: string }[];
  readonly homes: Record<string, GridPoint>;
  readonly nextRoam: Record<string, number>;
  readonly waveIndex: number;
  readonly nextWave: number | null;
  readonly draws: number;
}

/**
 * The infected side of a match: spawning (initial + hordes), wandering,
 * infection and rising. Walkers themselves are ordinary units owned by
 * `infected` in the UnitStore, so detection, chasing, attacking, damage and
 * death use exactly the Phase 6 combat rules. Engine-free and deterministic.
 */
export class InfectedDirector {
  private time = 0;
  private draws = 0;
  private readonly exposure = new Map<number, number>();
  private readonly infected = new Set<number>();
  private rising: { x: number; y: number; t: number; formerOwner: string }[] = [];
  private readonly homes = new Map<number, GridPoint>();
  private readonly nextRoam = new Map<number, number>();
  private waveIndex = 0;
  private nextWave: number | null;
  private readonly listeners = new Set<(e: InfectedEvent) => void>();
  private readonly unsubscribe: () => void;
  private readonly seed: number;
  private readonly schedule: { every: number; size: number; growth: number; max: number };

  constructor(
    private readonly world: WorldMap,
    private readonly units: UnitStore,
    private readonly buildings: BuildingStore,
    private readonly players: readonly PlayerId[],
    cfg: InfectedConfig = {},
  ) {
    this.seed = (cfg.seed ?? 1) >>> 0;
    this.schedule = {
      every: cfg.waveEverySeconds ?? HORDE.waveEverySeconds,
      size: cfg.waveSize ?? HORDE.waveSize,
      growth: cfg.waveGrowth ?? HORDE.waveGrowth,
      max: cfg.maxWaveSize ?? HORDE.maxWaveSize,
    };
    this.nextWave = cfg.waves === false ? null : (cfg.firstWaveSeconds ?? HORDE.firstWaveSeconds);
    this.unsubscribe = units.subscribe((e) => this.onUnit(e));
    const n = Math.max(0, Math.min(HORDE.maxInfected, Math.floor(cfg.initial ?? HORDE.initial)));
    if (n > 0) this.spawnInZones(n);
  }

  subscribe(l: (e: InfectedEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  /** Living infected units. */
  get count(): number {
    let n = 0;
    this.units.each((u) => {
      if (unitDef(u.type).infected) n++;
    });
    return n;
  }

  isInfected(unitId: number): boolean {
    return this.infected.has(unitId);
  }

  exposureOf(unitId: number): number {
    return this.exposure.get(unitId) ?? 0;
  }

  /** Seconds until the next horde, or null when hordes are off. */
  get secondsToWave(): number | null {
    return this.nextWave === null ? null : Math.max(0, this.nextWave - this.time);
  }

  get pendingRises(): number {
    return this.rising.length;
  }

  /** One tick: hordes, rising, bleeding, wandering (before the units move). */
  step(dt: number): void {
    if (!(dt > 0)) return;
    this.time += dt;
    if (this.nextWave !== null && this.time >= this.nextWave - 1e-9) {
      this.spawnWave();
      this.nextWave += this.schedule.every;
    }
    this.stepRising(dt);
    this.stepBleeding(dt);
    this.stepRoaming();
  }

  snapshot(): InfectedSnapshot {
    const rec = <T>(m: Map<number, T>): Record<string, T> => Object.fromEntries([...m.keys()].sort((a, b) => a - b).map((k) => [String(k), m.get(k)!]));
    return {
      time: this.time,
      exposure: rec(this.exposure),
      infected: [...this.infected].sort((a, b) => a - b),
      rising: this.rising.map((r) => ({ ...r })),
      homes: rec(new Map([...this.homes].map(([k, v]) => [k, { ...v }]))),
      nextRoam: rec(this.nextRoam),
      waveIndex: this.waveIndex,
      nextWave: this.nextWave,
      draws: this.draws,
    };
  }

  // ------------------------------------------------------------------ spawning

  /** Test / sandbox: add walkers at a tile (nearest open tiles), wandering around it. */
  spawnAt(at: GridPoint, count: number): number[] {
    const room = HORDE.maxInfected - this.count;
    const n = Math.max(0, Math.min(count, room));
    if (!n) return [];
    const spots = nearestOpenTiles(this.world.width, this.world.height, this.units.passable, at, n, this.occupied());
    return spots.map((s) => this.addWalker(s, s));
  }

  private spawnInZones(n: number): void {
    const zones = this.world.spawnRegions;
    if (!zones.length) return;
    const taken = this.occupied();
    for (let i = 0; i < zones.length; i++) {
      const z = zones[i]!;
      const share = Math.floor(n / zones.length) + (i < n % zones.length ? 1 : 0);
      // Scatter over the zone and a ring around it (deterministic draws), so they read as a crowd, not a formation.
      const r = z.radius + 2;
      let left = share;
      for (let k = 0; k < share * 12 && left > 0; k++) {
        const x = z.center.x + Math.floor(this.rand(k + 1) * (2 * r + 1)) - r;
        const y = z.center.y + Math.floor(this.rand(k + 1) * (2 * r + 1)) - r;
        const key = y * this.world.width + x;
        if (hyp(x - z.center.x, y - z.center.y) > r || !this.world.inBounds(x, y) || !this.units.passable(x, y) || taken.has(key)) continue;
        taken.add(key);
        this.addWalker({ x, y }, z.center);
        left--;
      }
      if (left > 0) this.spawnAt(z.center, left);
    }
  }

  /** A horde from a spawn zone marching (attack-move) on the next player's Command Center, round-robin. */
  private spawnWave(): void {
    const zones = this.world.spawnRegions;
    const targets = this.players.filter((p) => this.buildings.ownedBy(p).length > 0);
    if (!zones.length || !targets.length) return;
    const index = this.waveIndex++;
    const target = targets[index % targets.length]!;
    const zone = zones[index % zones.length]!;
    const size = Math.min(this.schedule.max, this.schedule.size + this.schedule.growth * index);
    const ids = this.spawnAt(zone.center, size);
    if (!ids.length) return;
    const own = this.buildings.ownedBy(target);
    const cc = own.find((b) => b.type === 'command_center') ?? own[0]!;
    const fp = buildingDef(cc.type).footprint;
    const goal = { x: cc.origin.x + Math.floor(fp.width / 2), y: cc.origin.y + Math.floor(fp.height / 2) };
    // They settle around the colony they attacked.
    for (const id of ids) this.homes.set(id, goal);
    this.units.attackMove(ids, goal);
    this.emit({ type: 'wave', index, count: ids.length, target, from: { ...zone.center } });
  }

  private addWalker(at: GridPoint, home: GridPoint): number {
    const u = this.units.add('walker', 'infected', at);
    this.homes.set(u.id, { x: home.x, y: home.y });
    this.nextRoam.set(u.id, this.time + this.pause(u.id));
    return u.id;
  }

  private occupied(): Set<number> {
    const s = new Set<number>();
    this.units.each((u) => s.add(Math.round(u.y) * this.world.width + Math.round(u.x)));
    return s;
  }

  // ------------------------------------------------------------------ infection

  private onUnit(e: UnitEvent): void {
    if (e.type === 'damaged') {
      const victim = e.unit;
      if (unitDef(victim.type).infected || !e.by) return;
      const attacker = this.units.get(e.by);
      if (!attacker || !unitDef(attacker.type).infected) return;
      const x = (this.exposure.get(victim.id) ?? 0) + INFECTION.biteExposure;
      this.exposure.set(victim.id, x);
      if (x >= INFECTION.infectAt && !this.infected.has(victim.id) && victim.health > 0) {
        this.infected.add(victim.id);
        this.emit({ type: 'infected', unitId: victim.id, ownerId: victim.ownerId });
      } else if (victim.health > 0 && !this.infected.has(victim.id)) {
        this.emit({ type: 'bitten', unitId: victim.id, exposure: x });
      }
    } else if (e.type === 'died') {
      const u = e.unit;
      if (unitDef(u.type).infected) {
        this.homes.delete(u.id);
        this.nextRoam.delete(u.id);
        return;
      }
      const byInfected = !!e.by && (() => {
        const a = this.units.get(e.by!);
        return !!a && !!unitDef(a.type).infected;
      })();
      const wasInfected = this.infected.has(u.id);
      this.exposure.delete(u.id);
      this.infected.delete(u.id);
      if (byInfected || wasInfected) this.rising.push({ x: Math.round(u.x), y: Math.round(u.y), t: INFECTION.riseSeconds, formerOwner: u.ownerId });
    }
  }

  private stepRising(dt: number): void {
    if (!this.rising.length) return;
    const still: typeof this.rising = [];
    for (const r of this.rising) {
      r.t -= dt;
      if (r.t > 1e-9) {
        still.push(r);
        continue;
      }
      if (this.count >= HORDE.maxInfected) continue;
      const spot = nearestOpenTiles(this.world.width, this.world.height, this.units.passable, { x: r.x, y: r.y }, 1)[0];
      if (!spot) continue;
      const id = this.addWalker(spot, spot);
      this.emit({ type: 'rose', zombieId: id, at: spot, formerOwner: r.formerOwner });
    }
    this.rising = still;
  }

  /** Infected units lose health; whole points per second, applied once per full second of infection. */
  private bleedClock = 0;
  private stepBleeding(dt: number): void {
    if (!this.infected.size) {
      this.bleedClock = 0;
      return;
    }
    this.bleedClock += dt;
    while (this.bleedClock >= 1 - 1e-9) {
      this.bleedClock -= 1;
      for (const id of [...this.infected].sort((a, b) => a - b)) this.units.damage(id, INFECTION.bleedPerSecond);
    }
  }

  // ------------------------------------------------------------------ wandering

  private stepRoaming(): void {
    const idle: { id: number; x: number; y: number }[] = [];
    this.units.each((u) => {
      if (unitDef(u.type).infected && u.order.kind === 'idle') idle.push({ id: u.id, x: u.x, y: u.y });
    });
    for (const u of idle) {
      const due = this.nextRoam.get(u.id);
      if (due === undefined) {
        this.nextRoam.set(u.id, this.time + this.pause(u.id));
        continue;
      }
      if (this.time < due) continue;
      const home = this.homes.get(u.id) ?? { x: Math.round(u.x), y: Math.round(u.y) };
      const dest = this.wanderTile(u.id, home);
      this.nextRoam.set(u.id, this.time + this.pause(u.id) + 4);
      if (dest) this.units.attackMove([u.id], dest);
    }
  }

  private wanderTile(id: number, home: GridPoint): GridPoint | null {
    const r = HORDE.roamRadius;
    for (let tries = 0; tries < 8; tries++) {
      const x = home.x + Math.floor(this.rand(id) * (2 * r + 1)) - r;
      const y = home.y + Math.floor(this.rand(id) * (2 * r + 1)) - r;
      if (this.world.inBounds(x, y) && this.units.passable(x, y)) return { x, y };
    }
    return null;
  }

  private pause(id: number): number {
    return HORDE.roamPauseMin + this.rand(id) * (HORDE.roamPauseMax - HORDE.roamPauseMin);
  }

  /** Deterministic draw: a hash of (unit id, running counter, seed). */
  private rand(id: number): number {
    return hash2(id, this.draws++, this.seed);
  }

  private emit(e: InfectedEvent): void {
    for (const l of this.listeners) l(e);
  }
}
